import { LayerViewModel } from "@omnia/shared/layers/layerViewModel"
import { IDatalayerMapDependencies, INumericValueColor, isArray, isNumber, selectMany } from "../../omnia"
import { IFieldsService, INutrient, INutritionService, NutrientType } from "../../types"
import { sortPlansByCalcOrder } from "../../utils"
import { PlanReader } from "./plan-reader"
import { DashboardSelection } from "./selection"
import { IDashboardSettings, INutritionMapViewContext } from "./types"
import { ClosingRequirementsMapView } from "./view/closing-requirements"
import { NtrDashboardMap } from "./view/map"
import { OpeningBalanceMapView } from "./view/opening-balance"
import { PlanMapView } from "./view/plan"
import { PlanOpeningRequirementsMapView } from "./view/plan-opening-requirements"
import { RequirementsMapView } from "./view/requirements"
import { TotalContributionsMapView } from "./view/total-contributions"
import { NutritionMapView } from "./view/view"
import { DatalayerZone } from "@omnia/shared/layers/datalayerZone"

export class MapContext {

    private _nutrient: NutrientType;

    private _views: NutritionMapView[];

    private _view: NutritionMapView;
    private _viewKey: string;

    private _visibilityFilter: (lvm: LayerViewModel) => boolean;


    constructor(
        private nutritionSvc: INutritionService,
        private fieldSvc: IFieldsService,
        private planReader: PlanReader,
        private map: NtrDashboardMap,
        private mapDependencies: IDatalayerMapDependencies) {

    }

    dispose() {

        if (this.views) {
            this.views.forEach(v => v.hideLayers());
        }

    }

    public setup() {

        this._views = this.setupViews();

        this.setNutrient(this._nutrient, true);

    }


    private setupViews() {

        let datalayerContext = this.mapDependencies.createContext(() => this.map.getDataLayers());

        let viewContext: INutritionMapViewContext = {

            fieldSvc: this.fieldSvc,
            nutritionSvc: this.nutritionSvc,

            map: this.map,

            datalayerContext: datalayerContext,

            planReader: this.planReader

        }

        let views: NutritionMapView[] = RequirementsMapView.create(viewContext);

        views = views.concat(OpeningBalanceMapView.create(viewContext));

        views = views.concat(this.createPlanViews(viewContext));

        views = views.concat(PlanOpeningRequirementsMapView.create(viewContext));

        views = views.concat(ClosingRequirementsMapView.create(viewContext));

        views = views.concat(TotalContributionsMapView.create(viewContext));

        return views;

    }

    get nutrient(): NutrientType {
        return this._nutrient;
    }

    set nutrient(value: NutrientType) {
        this.setNutrient(value, false);
    }

    get views(): NutritionMapView[] {
        return this._views;
    }

    get view(): NutritionMapView {
        return this._view;
    }

    set view(value: NutritionMapView) {
        this.setView(value);
    }

    get colorKey(): INumericValueColor[] {
        if (this.view) {
            return this.view.getColorKey();
        }
    }

    public setNutrient(nutrient: NutrientType, setDefault: boolean) {

        let views = this._views.filter(v => v.checkNutrient(nutrient));
        let view: NutritionMapView;

        let viewKey = this._viewKey;

        if (this._viewKey) {
            view = views.find(v => v.key == viewKey);
        }

        if (!view && (views.length > 0) && setDefault) {
            view = views[0];
        }

        this.setView(view);

        this._viewKey = view ? view.key : viewKey;

    }

    public setView(view: NutritionMapView, reset?: boolean) {

        if (reset || (this._view != view)) {
            let previousView = this._view;
            this._view = view;
            this._viewKey = view ? view.key : null;
            this.updateMap(view, previousView);
        }

    }

    public setVisibilityFilter(filter: (lvm: LayerViewModel) => boolean) {
        this._visibilityFilter = filter;
        if (this._view) {
            this._view.showLayers(filter);
        }
    }

    public viewDefaultExtent() {
        this.map.viewDefaultExtent()
    }

    public viewItemExtent(item) {

        if (isArray(item)) {
            this.map.viewExtentForDataItems(item)
        }
        else {
            this.map.viewExtentForDataItem(item)
        }

    }

    private updateMap(view: NutritionMapView, previousView: NutritionMapView) {

        if (previousView) {
            previousView.hideLayers()
        }

        if (view) {
            view.showLayers(this._visibilityFilter, this.map.visibleLabels)
        }

    }


    public refreshViews() {

        this.views.forEach(v => v.refreshLayers());

        if (this._view && this._visibilityFilter) {
            this._view.showLayers(this._visibilityFilter)
        }

    }

    private createPlanViews(ctxt: INutritionMapViewContext) {

        let plans = ctxt.nutritionSvc.getPlans()

        plans = sortPlansByCalcOrder(plans, p => p)

        return selectMany(plans.map(p =>
            selectMany(p.Applications.map(a => PlanMapView.create(ctxt, p, a)))
        ));

    }

    public clearSelection() {
        this.map.clearSelection();
    }

    public selectCropping(croppingId: number, center: boolean = true) {

        let cropping = this.fieldSvc.findCropping(croppingId);

        let feature = this.map.findFeatureWithData(cropping);

        if (feature) {

            this.map.selectFeatures([feature]);

            if (center) {
                this.map.setCenter(feature);
            }

        }

    }

    public selectZoneOnCurrentView(croppingId: number, zoneNumber: number): boolean {

        if (this.view && isNumber(zoneNumber)) {

            let layers = this.view.getLayers();

            let layer = layers.find(l => (l.croppingId == croppingId));

            let feature = layer.getMapLayer().getFeatures().find(f => {
                let zone = <DatalayerZone>f.get("data");
                return zone && ((zone.zoneNumber || 0) == zoneNumber);
            });

            if (feature) {
                this.map.selectFeatures([feature]);
                return true;
            }
        }

    }

    public selectViewFromSelection(nutrient: INutrient, selection: DashboardSelection) {

        if (!selection) return;
        if (!nutrient) return;

        let views = this.views.filter(v => v.checkNutrient(nutrient.nutrient));
        let view: NutritionMapView;

        let mapSelection = this.map.getSelectedFeatures();

        if (selection.appId) {

            view = views.find(v => (v instanceof PlanMapView) && (v.getApplicationId() == selection.appId));

        }
        else if (selection.planId) {

            view = views.find(v => (v instanceof PlanOpeningRequirementsMapView) && (v.planId == selection.planId));

        }
        else if ((selection.closing == 1) || ((!selection.croppingId) && (selection.mapView == "closingReq"))) {

            view = views.find(v => (v instanceof ClosingRequirementsMapView));

        }
        else if ((selection.closing == -1) || ((!selection.croppingId) && (selection.mapView == "ob"))) {

            view = views.find(v => (v instanceof OpeningBalanceMapView));

        }
        else if ((!selection.croppingId) && (selection.mapView == "contrib")) {

            view = views.find(v => (v instanceof TotalContributionsMapView));

        }
        else if ((!selection.croppingId) && (selection.mapView == "ob")) {

            view = views.find(v => (v instanceof OpeningBalanceMapView));

        }
        else {

            view = views.find(v => v instanceof RequirementsMapView);

        }

        this.setView(view);

        if (mapSelection && (mapSelection.length == 1)) {

            let f = mapSelection[0];
            let zone = <DatalayerZone>f.get("data");

            if (zone && zone.layer && zone.layer.croppingId) {
                this.selectZoneOnCurrentView(zone.layer.croppingId, zone.zoneNumber || 0);
            }

        }

    }

    applySettings(settings: IDashboardSettings) {

        let planViews = this.views.filter(v => v instanceof PlanMapView).map(v => <PlanMapView>v);

        planViews.forEach(p => {
            p.showNutrientRates = settings.applications && settings.applications.showNutrientRates;
        });

        this.map.refreshSelectInteraction();

    }

    clearZoneOverrides() {

        this._views.forEach(v => {
            v.clearZoneOverrides()
        });

        this.map.refreshSelectInteraction()

    }

    public refreshLabels() {

        if (this._view) {
            this._view.setupLabels(this.map.visibleLabels)
        }

    }

}
